import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Calendar, Mail, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useAnnouncement } from "@/hooks/useSiteContent";

const DISMISS_KEY_PREFIX = "is_announcement_dismissed_";
const SHOW_DELAY_MS = 2000;

export default function AnnouncementPopup() {
  const { data } = useAnnouncement();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const reduce = useReducedMotion();
  const dialogRef = useRef(null);
  const primaryBtnRef = useRef(null);
  const previouslyFocused = useRef(null);

  const isAdminRoute = location.pathname.startsWith("/admin");
  const enabled = Boolean(data?.enabled) && !isAdminRoute;
  const version = data?.version || "v1";
  const dismissKey = `${DISMISS_KEY_PREFIX}${version}`;

  // First-visit gate: show once per version, then persist dismissal
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem(dismissKey)) return;
    } catch (_) {
      return;
    }
    const t = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [enabled, dismissKey]);

  const dismiss = () => {
    setOpen(false);
    try {
      window.localStorage.setItem(dismissKey, new Date().toISOString());
    } catch (_) {
      /* ignore */
    }
  };

  // Focus management + Escape to close
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement;
    const t = setTimeout(() => primaryBtnRef.current?.focus?.(), 60);

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        dismiss();
      } else if (e.key === "Tab") {
        // Simple focus trap within the modal
        const focusables = dialogRef.current?.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);

    // Lock scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enabled) return null;

  const primaryUrl = data?.primary_url || "";
  const primaryLabel = data?.primary_label || "Book a meeting";
  const contactEmail = data?.contact_email || "agastya@ingredientsignal.com";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          data-testid="announcement-popup-backdrop"
        >
          <div
            className="absolute inset-0 bg-brand-deep/50 backdrop-blur-sm"
            onClick={dismiss}
            aria-hidden="true"
          />
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="announcement-headline"
            data-testid="announcement-popup"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md bg-brand-cream border border-brand-border rounded-2xl shadow-[0_40px_100px_-20px_rgba(47,61,30,0.5)] overflow-hidden"
          >
            {/* Kicker strip */}
            <div className="bg-brand-deep text-brand-cream px-6 py-2.5 flex items-center justify-between">
              <span className="inline-flex items-center gap-2 text-[10px] font-semibold tracking-[0.28em] uppercase text-brand-accent">
                <Calendar className="w-3.5 h-3.5" strokeWidth={1.8} />
                Meet us in person
              </span>
              <button
                type="button"
                onClick={dismiss}
                data-testid="announcement-popup-close"
                aria-label="Close announcement"
                className="inline-flex items-center justify-center w-7 h-7 rounded-full text-brand-cream/80 hover:text-brand-cream hover:bg-brand-cream/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-6">
              <h2
                id="announcement-headline"
                data-testid="announcement-popup-headline"
                className="font-display text-2xl text-brand-deep leading-tight tracking-[-0.01em]"
              >
                {data?.headline || "We're attending SupplySide Global"}
              </h2>
              <p
                data-testid="announcement-popup-body"
                className="mt-3 text-sm text-brand-warm leading-relaxed"
              >
                {data?.body ||
                  "The IngredientSignal team will be on the floor. Let's meet."}
              </p>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                {primaryUrl && (
                  <a
                    ref={primaryBtnRef}
                    href={primaryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="announcement-popup-primary"
                    onClick={dismiss}
                    className="inline-flex items-center justify-center gap-2 bg-brand-olive hover:bg-brand-hover text-brand-cream px-5 py-3 rounded-full text-sm font-medium tracking-wide transition-colors flex-1"
                  >
                    {primaryLabel}
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                )}
                <a
                  href={`mailto:${contactEmail}`}
                  data-testid="announcement-popup-secondary"
                  onClick={dismiss}
                  className="inline-flex items-center justify-center gap-2 border border-brand-border hover:border-brand-olive text-brand-deep px-5 py-3 rounded-full text-sm font-medium tracking-wide transition-colors flex-1"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.7} />
                  Connect with us
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
